import { Avatar, Box, ButtonBase, Typography } from "@mui/material";
import MooneCrescentLogo from "Assets/Images/Images/moone-crescent.svg";
import { Colors } from "Components/Layout/Themes/Colors";
import type { StoryAuthorKind } from "Components/Stories/StoriesSection";
import type { ReactNode } from "react";

/**
 * Ring around the circle: "unread"/"read" mirror the source's watched state (solid white vs
 * translucent light grey); "none" draws no ring but keeps the circle dimensions for circles that
 * carry no watched state — the own-story avatar with nothing published.
 */
export type StoryAvatarRingVariant = "unread" | "read" | "none";

type Props = {
  name: string;
  imageUrl?: string;
  authorKind?: StoryAuthorKind;
  ringVariant?: StoryAvatarRingVariant;
  /** Rendered inside the MUI Avatar when there is no image (e.g. the own avatar's initials). */
  avatarContent?: ReactNode;
  /** Small adornment over the circle's bottom-right corner (e.g. the own-story plus button). */
  badge?: ReactNode;
  /** Without it the circle renders as a non-interactive element (not a focusable button). */
  onClick?: () => void;
};

/**
 * Avatar image per author kind: Mo.one stories always show the crescent app mark (a dark-on-white
 * disc that fills the circle as-is); every other author shows the persona's avatar (letter fallback when absent).
 * Shared by the bar avatar and the viewer's author header.
 */
export const getStoryAvatarSrc = (
  authorKind: StoryAuthorKind,
  imageUrl?: string,
): string | undefined =>
  authorKind === "moone" ? MooneCrescentLogo : imageUrl;

/**
 * Circle geometry (design spec). The ring is the outer circle with an off-centre inner circle cut
 * out of it — the cut-out sits 2px up-left of the outer centre, so the ring is 2px thick at the
 * top-left and 6px at the bottom-right. The photo is concentric with the cut-out, leaving a uniform
 * transparent gap between photo and ring that shows whatever is behind the bar.
 */
export const STORY_CIRCLE_SIZE = 92;
const INNER_CUTOUT_SIZE = 84;
const INNER_CUTOUT_OFFSET = 2;
const AVATAR_SIZE = 78;

const OUTER_CENTER = STORY_CIRCLE_SIZE / 2;
const INNER_CENTER = OUTER_CENTER - INNER_CUTOUT_OFFSET;
const AVATAR_INSET = INNER_CENTER - AVATAR_SIZE / 2;

/** Closed circle sub-path (two half-arcs) starting at the circle's top point. */
const circlePath = (cx: number, cy: number, r: number): string =>
  `M ${cx} ${cy - r} a ${r} ${r} 0 1 0 0 ${2 * r} a ${r} ${r} 0 1 0 0 ${-2 * r} Z`;

// Even-odd fill subtracts the inner circle from the outer one, so the middle stays truly
// transparent (no mask ids, no vendor prefixes, crisp at any device pixel ratio).
const RING_PATH = [
  circlePath(OUTER_CENTER, OUTER_CENTER, STORY_CIRCLE_SIZE / 2),
  circlePath(INNER_CENTER, INNER_CENTER, INNER_CUTOUT_SIZE / 2),
].join(" ");

const RING_FILLS: Record<
  Exclude<StoryAvatarRingVariant, "none">,
  { color: string; opacity: number }
> = {
  unread: { color: Colors.White, opacity: 1 },
  read: { color: Colors.LightGray, opacity: 0.6 },
};

/** 14px — larger than the caption default so the names read on the phone hero. */
export const LABEL_FONT_SIZE = "0.875rem";

const COLUMN_SX = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 2,
  flexShrink: 0,
  width: STORY_CIRCLE_SIZE,
} as const;

export const StoryAvatar = ({
  name,
  imageUrl,
  authorKind = "merchant",
  ringVariant = "unread",
  avatarContent,
  badge,
  onClick,
}: Props) => {
  const ringFill = ringVariant === "none" ? undefined : RING_FILLS[ringVariant];

  const content = (
    <>
      <Box
        sx={{
          position: "relative",
          width: STORY_CIRCLE_SIZE,
          height: STORY_CIRCLE_SIZE,
        }}
      >
        {ringFill && (
          <Box
            component="svg"
            viewBox={`0 0 ${STORY_CIRCLE_SIZE} ${STORY_CIRCLE_SIZE}`}
            aria-hidden
            sx={{ position: "absolute", inset: 0, display: "block" }}
          >
            <path
              d={RING_PATH}
              fillRule="evenodd"
              fill={ringFill.color}
              fillOpacity={ringFill.opacity}
            />
          </Box>
        )}
        <Avatar
          src={getStoryAvatarSrc(authorKind, imageUrl)}
          alt={name}
          sx={{
            position: "absolute",
            top: AVATAR_INSET,
            left: AVATAR_INSET,
            width: AVATAR_SIZE,
            height: AVATAR_SIZE,
          }}
        >
          {avatarContent}
        </Avatar>
        {badge && (
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
            }}
          >
            {badge}
          </Box>
        )}
      </Box>
      <Typography
        variant="caption"
        noWrap
        sx={{
          maxWidth: STORY_CIRCLE_SIZE,
          textAlign: "center",
          fontSize: LABEL_FONT_SIZE,
        }}
      >
        {name}
      </Typography>
    </>
  );

  // A circle with no action must not be a focusable button — keyboard and screen-reader users
  // would land on a control that does nothing.
  if (!onClick) {
    return <Box sx={COLUMN_SX}>{content}</Box>;
  }

  return (
    <ButtonBase onClick={onClick} sx={COLUMN_SX}>
      {content}
    </ButtonBase>
  );
};
